import type {AppProps} from 'next/app';
import {ApolloProvider} from '@apollo/client';
import {useApollo} from '@/framework/client';
import React, {useEffect, useRef} from 'react';
import {initializeCoreIntegration} from '@/_application/core/integration';
import {hydrateTokensFromSession} from '@/_application/modules/auth/model/in-memory-token-store';

interface QueryProviderProps {
    pageProps: AppProps['pageProps'];
}

export default function QueryProvider({pageProps, children}: React.PropsWithChildren<QueryProviderProps>) {
    const apolloClient = useApollo(pageProps);
    const isInitializedRef = useRef(false);

    // Initialize core module integration SYNCHRONOUSLY during render.
    // initializeCoreIntegration only sets module-level singleton variables
    // (unifiedClient, authTokenProvider, customHeadersGetter) — no DOM mutations,
    // no subscriptions — so it is safe to call during render.
    //
    // Previously this ran in useEffect (async, after render), which meant
    // child useEffect hooks could fire GraphQL requests BEFORE the integration
    // was wired, resulting in missing headers (store-code, API key) and no token.
    if (apolloClient && !isInitializedRef.current) {
        initializeCoreIntegration(apolloClient);
        isInitializedRef.current = true;
    }

    // Hydrate in-memory tokens from NextAuth session after mount.
    // NextAuth getSession() is async (calls /api/auth/session), so it runs in useEffect.
    // Apollo Client will initially have no token, but subsequent requests will use it.
    useEffect(() => {
        hydrateTokensFromSession();
    }, []);

    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
}
